import { ReactNode, useState } from "react";
import {
  Button,
  Combobox,
  Group,
  ScrollArea,
  Text,
  useCombobox,
} from "@mantine/core";
import { IconCheck, IconChevronDown, IconSearch } from "@tabler/icons-react";
import { normalizeString } from "@jasper/shared";

type FilterOption = { value: string; label: string };

/*
  Toolbar filter: a pill button that reads « Statut · Tous » and grows with the selected
  label, opening a list of options. The first option stands for "no filter" (`null`).
*/
export const FilterDropdown = ({
  icon,
  label,
  options,
  value,
  onChange,
  searchPlaceholder,
}: {
  icon: ReactNode;
  label: string;
  options: FilterOption[];
  value: string | null;
  onChange: (value: string | null) => void;
  // Adds a search field above the options when set.
  searchPlaceholder?: string;
}) => {
  const [search, setSearch] = useState("");
  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      setSearch("");
    },
    onDropdownOpen: () => {
      if (searchPlaceholder) {
        combobox.focusSearchInput();
      }
    },
  });

  const [defaultOption] = options;
  const selectedOption =
    options.find(option => option.value === value) ?? defaultOption;
  const normalizedSearch = normalizeString(search.trim());
  const visibleOptions = options.filter(option =>
    normalizeString(option.label).includes(normalizedSearch)
  );

  return (
    <Combobox
      store={combobox}
      width={240}
      position="bottom-start"
      onOptionSubmit={submitted => {
        onChange(submitted === defaultOption.value ? null : submitted);
        combobox.closeDropdown();
      }}
    >
      <Combobox.Target>
        <Button
          radius="full"
          variant={value ? "light" : "default"}
          leftSection={icon}
          rightSection={<IconChevronDown size={14} />}
          onClick={() => combobox.toggleDropdown()}
        >
          <Text span inherit c={value ? undefined : "ink.4"} fw={400}>
            {label}
          </Text>
          &nbsp;
          <Text span inherit fw={600}>
            {selectedOption.label}
          </Text>
        </Button>
      </Combobox.Target>

      <Combobox.Dropdown>
        {searchPlaceholder && (
          <Combobox.Search
            value={search}
            onChange={event => setSearch(event.currentTarget.value)}
            placeholder={searchPlaceholder}
            leftSection={<IconSearch size={15} />}
          />
        )}
        <Combobox.Options>
          <ScrollArea.Autosize mah={320} type="scroll">
            {visibleOptions.length === 0 && (
              <Combobox.Empty>Aucun résultat</Combobox.Empty>
            )}
            {visibleOptions.map(option => {
              const isSelected = option.value === selectedOption.value;
              return (
                <Combobox.Option
                  key={option.value}
                  value={option.value}
                  active={isSelected}
                >
                  <Group justify="space-between" wrap="nowrap">
                    <Text span inherit fw={isSelected ? 600 : undefined}>
                      {option.label}
                    </Text>
                    {isSelected && <IconCheck size={15} />}
                  </Group>
                </Combobox.Option>
              );
            })}
          </ScrollArea.Autosize>
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
};
